import React from "react";

/**
 * Función mejorada para reportar errores detallados al backend (estilo Laravel)
 */
let lastErrorPayload = null;
let lastErrorTime = 0;

export const reportErrorToBackend = async (error, errorInfo = {}) => {
  const currentTime = Date.now();
  const errorMessage = error?.message || "Unknown Error";

  // Evitar duplicados (ej: por StrictMode o re-renders rápidos)
  // Si el mismo error ocurre en menos de 2 segundos, lo ignoramos
  if (lastErrorPayload === errorMessage && (currentTime - lastErrorTime) < 2000) {
    return;
  }

  lastErrorPayload = errorMessage;
  lastErrorTime = currentTime;

  try {
    const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
    let user_id = "guest";
    let userName = "Invitado";
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        user_id = user.id || user.user_id || "unknown";
        userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || "Usuario";
      } catch (e) {
        user_id = "unknown_user";
      }
    }

    // Extraer archivo y línea del stack trace
    const stack = error?.stack || "";
    const stackLines = stack.split('\n');
    
    // Buscamos la primera línea del stack que no sea el mensaje de error
    // Normalmente la línea 1 o 2 contiene el archivo y línea
    let file = "unknown";
    let line = "0";
    
    const stackLine = stackLines.find(l => l.includes("http") || l.includes(".js"));
    if (stackLine) {
        const match = stackLine.match(/(http.*):(\d+):(\d+)/) || stackLine.match(/at (.*):(\d+):(\d+)/);
        if (match) {
            file = match[1].split('/').pop(); // Solo el nombre del archivo
            line = match[2];
        }
    }

    const payload = {
      status_code: "500", 
      message: error?.message || "Frontend Error",
      file: file,
      line: line,
      path: window.location.pathname,
      user_id: user_id,
      user_name: userName,
      stack: stack,
      componentStack: errorInfo?.componentStack || "",
      parameters: JSON.stringify({
        browser: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      })
    };

    const backendUrl = process.env.REACT_APP_URL_BACKEND || "http://localhost:9000";
    const url = `${backendUrl}/api/v1/error_logs`;
    
    console.log(`Reporting detailed error to: ${url}`);
    
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error("Failed to send detailed error log to backend:", err);
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      lastError: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const isDevelopment = process.env.NODE_ENV === "development";

    if (this.state.lastError === error.message) {
      return;
    }

    this.setState({ lastError: error.message });

    if (isDevelopment) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    reportErrorToBackend(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const isDevelopment = process.env.NODE_ENV === "development";

    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <h1 style={styles.title}>Ocurrió un error inesperado</h1>
            <p style={styles.text}>
              Lo sentimos, algo salió mal en la aplicación. Hemos registrado el problema para solucionarlo.
            </p>
            <button style={styles.button} onClick={this.handleReload}>
              Recargar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", width: "100vw", backgroundColor: "#f8f9fa", fontFamily: "sans-serif" },
  card: { textAlign: "center", padding: "40px", borderRadius: "12px", backgroundColor: "#ffffff", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", maxWidth: "500px" },
  title: { color: "#dc3545", fontSize: "24px", marginBottom: "16px" },
  text: { color: "#6c757d", fontSize: "16px", marginBottom: "24px", lineHeight: "1.5" },
  button: { backgroundColor: "#007bff", color: "#fff", border: "none", padding: "12px 24px", borderRadius: "6px", cursor: "pointer", fontSize: "16px" },
};

export default ErrorBoundary;
